// src/services/userService.js
import api from "../utils/api";

/**
 * Normalise API errors so UI gets useful messages
 */
function extractErrorMessage(err, fallback = "Request failed") {
  // axios error
  const msg =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback;
  return msg;
}

/**
 * Get all users
 */
export const getAllUsers = async () => {
  try {
    const res = await api.get("/users");
    return res.data?.data?.users || [];
  } catch (err) {
    console.error("Error fetching users:", err);
    throw new Error(extractErrorMessage(err, "Failed to fetch users"));
  }
};

/**
 * Create a new user
 */
export const createUser = async (userData) => {
  try {
    const res = await api.post("/users", userData);
    return {
      user: res.data?.data?.user,
    };
  } catch (err) {
    console.error("Error creating user:", err);
    throw new Error(extractErrorMessage(err, "Failed to create user"));
  }
};

export const sendUserEmail = async (id, { type, password }) => {
  try {
    const res = await api.post(`/users/${id}/send-email`, { type, password });
    return res.data;
  } catch (err) {
    console.error("Error sending user email:", err);
    throw new Error(extractErrorMessage(err, "Failed to send email"));
  }
};

/**
 * Get user by ID (includes pin/key)
 */
export const getUserById = async (id) => {
  try {
    const res = await api.get(`/users/${id}`);
    return res.data?.data?.user;
  } catch (err) {
    console.error(`Error fetching user ${id}:`, err);
    throw new Error(extractErrorMessage(err, "Failed to fetch user"));
  }
};

/**
 * Update user
 */
export const updateUser = async (id, userData) => {
  try {
    const res = await api.put(`/users/${id}`, userData);
    return res.data?.data?.user;
  } catch (err) {
    console.error(`Error updating user ${id}:`, err);
    throw new Error(extractErrorMessage(err, "Failed to update user"));
  }
};

/**
 * Delete user
 */
export const deleteUser = async (id) => {
  try {
    const res = await api.delete(`/users/${id}`, {
      // Avoid axios throw + console stack for expected 409; still visible in Network tab
      validateStatus: (status) => status === 200 || status === 409,
    });
    if (res.status === 409) {
      const msg =
        res.data?.message ||
        res.data?.details ||
        "Cannot delete this user — they are still referenced in another system.";
      const err = new Error(msg);
      err.status = 409;
      err.details = res.data?.details;
      throw err;
    }
    return res.data;
  } catch (err) {
    if (err.status === 409) throw err;
    throw new Error(extractErrorMessage(err, "Failed to delete user"));
  }
};

/**
 * Get current user profile
 */
export const getCurrentUser = async () => {
  try {
    const res = await api.get("/users/me");
    return res.data?.data?.user;
  } catch (err) {
    console.error("Error fetching current user:", err);
    throw new Error(extractErrorMessage(err, "Failed to fetch current user"));
  }
};

/**
 * Assignable users
 */
export const getAssignableUsers = async () => {
  try {
    const res = await api.get("/users/assignable");
    return res.data?.data?.users || [];
  } catch (err) {
    console.error("Error fetching assignable users:", err);
    throw new Error(extractErrorMessage(err, "Failed to fetch assignable users"));
  }
};